import { collectListings, choicesFor, resolveGameURL, resetListings } from './bb-listing-data-v15.js';
import { fastChoices } from './bb-fast-facets-v15.js';
import { pokemonScope, populateSets } from './bb-set-order-v19.js';
const instances = new WeakMap();
const warmedGames = new Map();
// Start optional work after the page's initial resources have loaded. Browsers
// without idle callbacks use a small delay; hidden tabs wait until visible.
function afterPageLoad(callback, signal) {
  let timer, idle;
  const schedule = () => {
    if (signal.aborted) return;
    if (document.hidden) { document.addEventListener('visibilitychange', visible, { signal }); return; }
    if (typeof window.requestIdleCallback === 'function') idle = window.requestIdleCallback(() => { if (!signal.aborted) callback(); }, { timeout: 1500 });
    else timer = setTimeout(() => { if (!signal.aborted) callback(); }, 150);
  };
  const visible = () => { if (!document.hidden) { document.removeEventListener('visibilitychange', visible); schedule(); } };
  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule, { once: true, signal });
  signal.addEventListener('abort', () => { clearTimeout(timer); if (idle !== undefined) window.cancelIdleCallback?.(idle); }, { once: true });
}
export function queryURL(form, names) {
  const url = new URL(form.action, location.origin);
  if (url.origin !== location.origin) throw new Error('Filters must stay on this store.');
  ['view', 'page', 'section_id', 'bb_sort', 'bb_page', 'bb_client', 'bb_find', ...names].forEach(name => url.searchParams.delete(name));
  for (const [name, value] of new FormData(form)) {
    if (!name || names.includes(name) || ['view', 'page', 'section_id', 'bb_sort', 'bb_page', 'bb_client', 'bb_find'].includes(name)) continue;
    url.searchParams.delete(name);
    if (String(value).trim()) url.searchParams.append(name, value);
  }
  return url;
}
export class LinkedFilters {
  constructor(form) {
    this.form = form;
    this.names = [form.dataset.setParam, form.dataset.artistParam, form.dataset.rarityParam].filter(Boolean);
    this.selects = this.names.map(name => form.elements.namedItem(name));
    if (this.selects.some(select => !select || select.tagName !== 'SELECT')) return;
    const gameSelect = form.querySelector('select[data-bb-game-select]');
    this.game = gameSelect?.tagName === 'SELECT' ? gameSelect : null;
    this.defaultAction = form.action;
    if (this.game) this.game.disabled = false;
    // Native facets may omit an active value beyond their choice limit.
    if (form.closest?.('#bb-catalog')) {
      const url = new URL(location.href);
      this.selects.forEach((select, index) => {
        const value = url.searchParams.get(this.names[index]);
        if (!value) return;
        if (![...select.options].some(option => option.value === value)) {
          const option = document.createElement('option'); option.value = value; option.textContent = value; select.append(option);
        }
        select.value = value;
      });
    }
    this.status = form.querySelector('[data-linked-status]');
    this.buttons = [...form.querySelectorAll('button[type="submit"]')];
    this.events = new AbortController();
    const options = { signal: this.events.signal };
    const ensureChoices = event => {
      if ((!this.loaded || Date.now() - this.loadedAt > 120000) && !this.loading && this.selects.includes(event.target)) this.refresh(this.selects.indexOf(event.target));
    };
    form.addEventListener('focusin', ensureChoices, options);
    form.addEventListener('pointerdown', ensureChoices, options);
    const reload = form.querySelector('[data-bb-refresh-choices]');
    if (reload) {
      reload.hidden = false;
      reload.addEventListener('click', async () => { this.request?.abort(); await resetListings(); this.loaded = false; this.refresh(0); }, options);
    }
    form.addEventListener('change', event => {
      if (event.target === this.game) {
        this.gameScope();
        this.selects.forEach((select, index) => { this.empty(select, index); select.disabled = true; });
        this.refresh(0);
        return;
      }
      const index = this.selects.indexOf(event.target);
      if (['sort_by', 'view', 'bb_find'].includes(event.target.name) || index < 0 && !this.loaded) return;
      this.refresh(index < 0 ? 0 : index);
    }, options);
    const reset = form.querySelector('[data-clear-linked-filters]');
    if (reset) {
      reset.hidden = false;
      reset.addEventListener('click', () => {
        if (this.game) { this.game.value = ''; this.gameScope(); }
        this.selects.forEach(select => { select.value = ''; }); this.refresh(0);
      }, options);
    }
    if (this.game) {
      this.gameScope();
      form.addEventListener('submit', event => {
        event.preventDefault();
        // GET forms drop query parameters from their action; keep a configured search-game scope.
        location.assign(queryURL(this.form, []).href);
      }, options);
    }
    this.orderInitialSets();
    // Native collection filters stay usable while the complete choice list refreshes.
    this.status.textContent = this.game ? 'Choose a game to explore its sets and artists, or browse across all games.' : 'Choose a set or artist to load matching choices.';
    afterPageLoad(async () => {
      // Preloading must never start a complete product scan. The existing
      // on-demand fallback remains available when Flow tags are incomplete.
      if (!this.loading && (!this.loaded || Date.now() - this.loadedAt >= 120000)) await this.refresh(0, { background: true });
      if (this.loaded && !this.events.signal.aborted) this.preloadGames();
    }, this.events.signal);
  }
  empty(select, index) {
    const option = document.createElement('option'); option.value = ''; option.textContent = ['All sets','All artists','All rarities'][index];
    select.replaceChildren(option); select.value = '';
  }
  orderInitialSets() {
    const select = this.selects[0], selected = select.value;
    const choices = [...select.options].filter(option => option.value).map(option => [option.value, null, option]);
    populateSets(select, choices, selected, pokemonScope(this.form.action));
  }
  gameScope() {
    const url = resolveGameURL(this.game.value || this.defaultAction);
    if (url.origin !== location.origin) throw new Error('Choose a game on this store.');
    this.form.action = url.href;
    const name = this.game.selectedOptions?.[0]?.textContent || '';
    this.form.closest('.bb-finder-panel')?.querySelectorAll('[data-bb-game-label]').forEach(label => {
      const selected = Boolean(this.game.value) && label.dataset.bbGameLabel === name;
      label.dataset.selected = String(selected);
      if (label.tagName === 'BUTTON') label.setAttribute('aria-pressed', String(selected));
    });
  }
  async preloadGames() {
    const connection = navigator.connection;
    if (!this.game || connection?.saveData || ['slow-2g', '2g'].includes(connection?.effectiveType)) return;
    const scopes = [...new Set([...this.game.options].flatMap(option => {
      if (!option.value) return [];
      try { return [resolveGameURL(option.value).href]; } catch { return []; }
    }))];
    let next = 0;
    await Promise.all(Array.from({ length: Math.min(2, scopes.length) }, async () => {
      while (next < scopes.length && !this.events.signal.aborted) {
        const scope = scopes[next++], previous = warmedGames.get(scope);
        if (previous && Date.now() - previous < 120000) continue;
        warmedGames.set(scope, Date.now());
        try {
          const result = await fastChoices(scope, this.selects.map(() => ''), 0, this.events.signal);
          if (!result) warmedGames.delete(scope);
        } catch { warmedGames.delete(scope); }
      }
    }));
  }
  async refresh(primary, { background = false } = {}) {
    this.request?.abort();
    const request = new AbortController();
    this.request = request;
    const values = this.selects.map(select => select.value);
    this.loading = true;
    this.form.setAttribute('aria-busy', 'true');
    if (!background) this.status.textContent = 'Updating matching choices…';
    try {
      const scope = queryURL(this.form, this.names);
      const fast = await fastChoices(scope, values, primary, request.signal);
      if (fast) {
        if (request.signal.aborted || this.request !== request || this.events.signal.aborted) return;
        const collator = new Intl.Collator('en-AU', { numeric: true, sensitivity: 'base' });
        fast.replacements.forEach(choices => choices.sort((a, b) => collator.compare(a[0], b[0])));
        this.replaceChoices(fast.replacements, fast.values);
        this.status.textContent = (fast.cleared ? 'The previous ' + fast.cleared + ' was cleared because it does not match. ' : '') + 'Choose a set, an artist, or both, then explore.';
        return;
      }
      if (background) return;
      const data = await collectListings(scope, request.signal);
      if (request.signal.aborted || this.request !== request || this.events.signal.aborted) return;
      const fields = ['set', 'artist', 'rarity'].slice(0,this.selects.length);
      let cleared = '';
      const matchingChoices = index => choicesFor(data.rows.filter(row => fields.every((field, other) => other === index || !values[other] || row[field] === values[other])), fields[index], '', '');
      // Keep the field just changed, clearing older incompatible selections first.
      for (const index of [...fields.keys()].filter(index=>index!==primary).concat(primary)) {
        if (values[index] && !matchingChoices(index).some(choice=>choice[0]===values[index])) {cleared=fields[index];values[index]='';}
      }
      const replacements = fields.map((field,index)=>matchingChoices(index));
      this.replaceChoices(replacements, values);
      const missing = data.rows.filter(row => !row.set || !row.artist).length;
      this.status.textContent = (cleared ? 'The previous ' + cleared + ' was cleared because it does not match. ' : '') +
        (this.game ? (this.game.selectedOptions?.[0]?.textContent || 'All games') + ' · ' + data.total + (data.total === 1 ? ' listing' : ' listings') + '. Choose a set, an artist, or both, then explore.' : 'Choose a set, an artist, or both, then show matching cards.') +
        (data.newer ? ' New cards are arriving; reload for the latest additions.' : '') +
        (data.limited ? ' Shopify limits this broad keyword search. Browse a game collection for complete choices.' : '') +
        (missing ? ' Some listings do not have both card details filled in.' : '');
    } catch (error) {
      if (request.signal.aborted || this.request !== request || this.events.signal.aborted) return;
      if (!background) this.status.textContent = 'The full choice list could not load. You can still submit your selected filters, or reload to try again.';
    } finally {
      if (this.request === request && !this.events.signal.aborted) {
        this.selects.forEach(select => { select.disabled = false; });
        this.loading = false; this.form.setAttribute('aria-busy', 'false');
        this.buttons.forEach(button => { button.disabled = false; });
      }
    }
  }
  replaceChoices(replacements, values) {
      replacements.forEach((choices, index) => {
        if (index === 0) {
          populateSets(this.selects[0], choices, values[0], pokemonScope(this.form.action));
          this.selects[0].disabled = false; return;
        }
        const empty = document.createElement('option'); empty.value = ''; empty.textContent = ['All sets','All artists','All rarities'][index];
        const options = choices.map(([value, count]) => {
          const option = document.createElement('option'); option.value = value; option.textContent = value + (count === null ? '' : ' (' + count + ')'); return option;
        });
        this.selects[index].replaceChildren(empty, ...options);
        this.selects[index].value = values[index];
        this.selects[index].disabled = false;
      });
      this.form.dispatchEvent(new CustomEvent('bb:linked-choices'));
      this.loaded = true;
      this.loadedAt = Date.now();
  }
  destroy() { this.request?.abort(); this.events?.abort(); }
}
function initialise(root = document) {
  if (document.querySelector('#bb-catalog')) {
    const destination = resolveGameURL(location.href);
    if (destination.href !== location.href) { location.replace(destination.href); return; }
  }
  root.querySelectorAll('[data-bb-linked-filters]').forEach(form => {
    if (!instances.has(form)) instances.set(form, new LinkedFilters(form));
  });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialise());
else initialise();
document.addEventListener('shopify:section:load', event => initialise(event.target));
document.addEventListener('shopify:section:unload', event => {
  event.target.querySelectorAll('[data-bb-linked-filters]').forEach(form => { instances.get(form)?.destroy(); instances.delete(form); });
});
